import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { createVariantOption, updateVariantOption, getVariantOptionDetails } from '../../Controller/VariantOptionController';
import { withNamespaces } from 'react-i18next';

/**
 * Form component for adding or editing variant options
 * @param {Object} props - Component props
 * @returns {JSX.Element} VariantOptionForm component
 */
const VariantOptionForm = (props) => {
  const { slack, lng, t } = props;
  const history = useHistory();
  const isEditMode = !!slack;
  
  // Form state
  const [formData, setFormData] = useState({
    variantOptionCode: '',
    label: '',
    status: '1'  // Active by default
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Load variant option data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchVariantOptionDetails();
    }
  }, [slack]);

  /**
   * Fetch variant option details for edit mode
   */
  const fetchVariantOptionDetails = async () => {
    setLoading(true);
    try {
      const response = await getVariantOptionDetails(slack);
      
      if (response.success && response.data) {
        setFormData({
          label: response.data.label || '',
          status: response.data.status === 1 ? '1' : '0'
        });
      } else {
        Utility.toastNotifications(response.message || t('variantOptions.form.errors.failedToFetchDetails'), 'Error', 'error');
        history.push(`/${lng}/settings/variant-options`);
      }
    } catch (error) {
      console.error('Error fetching variant option details:', error);
      Utility.toastNotifications(t('variantOptions.form.errors.errorFetchingDetails'), 'Error', 'error');
      history.push(`/${lng}/settings/variant-options`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form field changes
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear validation error when field is changed
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  /**
   * Validate form fields
   * @returns {boolean} Form validity
   */
  const validateForm = () => {
    const newErrors = {};
    
    // Validate label field
    if (!formData.label.trim()) {
      newErrors.label = t('variantOptions.form.validation.labelRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      let response;
      
      // Create or update based on mode
      if (isEditMode) {
        response = await updateVariantOption(slack, formData);
      } else {
        response = await createVariantOption(formData);
      }
      
      if (response.success) {
        Utility.toastNotifications(response.message || t(`variantOptions.form.success.${isEditMode ? 'updated' : 'created'}`), 'Success', 'success');
        history.push(`/${lng}/settings/variant-options`);
      } else {
        Utility.toastNotifications(response.message || t(`variantOptions.form.errors.failedTo${isEditMode ? 'Update' : 'Create'}`), 'Error', 'error');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} variant option:`, error);
      Utility.toastNotifications(t(`variantOptions.form.errors.errorWhile${isEditMode ? 'Updating' : 'Creating'}`), 'Error', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle cancellation and return to list
   */
  const handleCancel = () => {
    history.push(`/${lng}/settings/variant-options`);
  };

  return (
    <div className="variant-option-form-container">
      {/* Toast notifications handled by Utility */}
      <div className="page-header">
        <div className="page-title">
          <h4>
            <Link to={`/${lng}/settings/variant-options`}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            </Link>
            {isEditMode ? t('variantOptions.form.editTitle') : t('variantOptions.form.addTitle')}
          </h4>
        </div>
      </div>
      
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('variantOptions.form.loading')}</span>
              </div>
              <p className="mt-2">{t('variantOptions.form.loadingDetails')}</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row>                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('variantOptions.form.labels.label')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="label"
                      value={formData.label}
                      onChange={handleChange}
                      isInvalid={!!errors.label}
                      placeholder={t('variantOptions.form.placeholders.label')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.label}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      {t('variantOptions.form.helpText.labelExample')}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('variantOptions.form.labels.status')}</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        id="status-active"
                        name="status"
                        value="1"
                        checked={formData.status === '1'}
                        onChange={handleChange}
                        label={t('variantOptions.form.status.active')}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="status-inactive"
                        name="status"
                        value="0"
                        checked={formData.status === '0'}
                        onChange={handleChange}
                        label={t('variantOptions.form.status.inactive')}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="form-group mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="me-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {isEditMode ? t('variantOptions.form.buttons.updating') : t('variantOptions.form.buttons.saving')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      {isEditMode ? t('variantOptions.form.buttons.update') : t('variantOptions.form.buttons.save')}
                    </>
                  )}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="ms-2"
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  {t('variantOptions.form.buttons.cancel')}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default withNamespaces()(VariantOptionForm);
